import React, { useEffect, useRef, useState } from 'react';
import { Table, Button, Input, Space, Tooltip } from 'antd';
import { EditOutlined, SearchOutlined } from '@ant-design/icons';
import {colors} from '../theme';
import { useNavigate } from 'react-router-dom';
import { staticsApi, Static } from '../apiCalls/staticsApi';
import type { TableColumnType } from 'antd';
import type { FilterDropdownProps } from 'antd/es/table/interface';
import TableFiltersBar, { applyFiltersToColumns, useTableFilters } from '../components/TableFilters';
import PageTitle from '../components/PageTitle';

const StaticsList: React.FC = () => {
  const navigate = useNavigate();
  const [dataSource, setDataSource] = useState<Static[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const searchInput = useRef<any>(null);
  const { filters, handleTableChange, clearAllFilters, removeFilterValue } = useTableFilters<Static>();

  useEffect(() => {
    fetchStatics();
  }, []);

  const fetchStatics = async () => {
    setLoading(true);
    const statics = await staticsApi.getStatics();
    setDataSource(statics);
    setLoading(false);
  };

  const handleSearch = (selectedKeys: string[], confirm: FilterDropdownProps['confirm'], dataIndex: string) => {
    confirm();
  };

  const handleReset = (clearFilters: () => void) => {
    clearFilters();
  };

  const getColumnSearchProps = (dataIndex: string): TableColumnType<any> => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
          style={{ marginBottom: 8, display: 'block' }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Buscar
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => {
              clearFilters && handleReset(clearFilters);
              confirm({ closeDropdown: false });
              close();
            }}
          >
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) => <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />,
    onFilter: (value, record) =>
      record[dataIndex]
        .toString()
        .toLowerCase()
        .includes((value as string).toLowerCase()),
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
  });

  const columns = [
    {
      title: 'Actions',
      key: 'actions',
      fixed: 'left' as const,
      width: 80,
      align: 'center' as const,
      render: (_: any, record: Static) => (
          <Tooltip title="Edit">
            <Button
              type="text"
              size="small"
              style={{color: colors.primary}}
              icon={<EditOutlined />}
              onClick={() => navigate(`/statics/${record.id}/edit`)}
            />
          </Tooltip>
      ),
    },
    {
      title: 'Code',
      dataIndex: 'code',
      key: 'code',
      width: 400,
      ...getColumnSearchProps('code'),
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      ...getColumnSearchProps('name'),
    }
  ];
  const tableColumns = applyFiltersToColumns(columns, filters);

  return (
      <div style={{padding: '24px 32px'}}>
        <PageTitle title="List of Statics" />
        <TableFiltersBar
          columns={tableColumns}
          filters={filters}
          onClearAll={clearAllFilters}
          onRemove={removeFilterValue}
        />
        <Table
            dataSource={dataSource}
            columns={tableColumns}
            rowKey="id"
            loading={loading}
            pagination={{defaultPageSize: 50, pageSizeOptions: [10, 25, 50]}}
            scroll={{x: 'max-content', y: 'calc(100vh - 300px)'}}
            onChange={handleTableChange}
        />
      </div>
  );
};

export default StaticsList;
